import {
  $createHorizontalRuleNode,
  $isHorizontalRuleNode,
  HorizontalRuleNode,
} from "@lexical/extension";
import { $isAutoLinkNode, $isLinkNode } from "@lexical/link";
import {
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  CHECK_LIST,
  CODE,
  HEADING,
  INLINE_CODE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK,
  ORDERED_LIST,
  QUOTE,
  STRIKETHROUGH,
  UNORDERED_LIST,
  type ElementTransformer,
  type MultilineElementTransformer,
  type TextMatchTransformer,
  type Transformer,
} from "@lexical/markdown";
import { $isElementNode, type ElementNode, type LexicalNode } from "lexical";
import {
  ATTACHMENT_NODE_TYPE,
  attachmentMarkdown,
  CORE_TOKEN_REGISTRY,
  readAttachment,
  type EditorPreset,
  type SerializedNodeLike,
  type TokenRegistry,
} from "../../index";
import { $isTokenNode, TokenNode } from "../tokens/node";

/**
 * `---`, `***`, or `___` on an otherwise empty paragraph, completed by Enter (or a trailing space).
 * @lexical/markdown ships no horizontal-rule transformer.
 */
export const HORIZONTAL_RULE: ElementTransformer = {
  dependencies: [HorizontalRuleNode],
  export: (node) => ($isHorizontalRuleNode(node) ? "---" : null),
  regExp: /^(---|\*\*\*|___)\s?$/,
  replace: (parentNode, _children, _match, isImport) => {
    const rule = $createHorizontalRuleNode();
    // While typing, keep the (now empty) paragraph below the rule so the caret has a home.
    if (isImport || parentNode.getNextSibling() !== null) {
      parentNode.replace(rule);
    } else {
      parentNode.insertBefore(rule);
    }
    rule.selectNext();
  },
  triggerOnEnter: true,
  type: "element",
};

/**
 * `[label](url)` becomes a link only when the URL passes the editor's policy, and a link leaves as
 * one only when it passes too: a stored or shared document may hold a link the policy refuses, and
 * whatever renders the Markdown next may not check it again. A refused link leaves as its text.
 */
export function createLinkTransformer(validateUrl: (url: string) => boolean): TextMatchTransformer {
  const replace = LINK.replace;
  const exportLink = LINK.export;
  return {
    ...LINK,
    ...(exportLink === undefined
      ? {}
      : {
          export: (node, exportChildren, exportFormat) =>
            $isLinkNode(node) && !$isAutoLinkNode(node) && !validateUrl(node.getURL())
              ? exportChildren(node)
              : exportLink(node, exportChildren, exportFormat),
        }),
    ...(replace === undefined
      ? {}
      : {
          replace: (textNode, match) => {
            // Groups 2 and 3 are the `<pointy>` and raw destination forms of LINK.importRegExp.
            const url = match[2] ?? match[3] ?? "";
            if (!validateUrl(url)) return;
            return replace(textNode, match);
          },
        }),
  };
}

/**
 * Export only: a token leaves as its Markdown fallback (its label, unless its definition says
 * otherwise). There is no Markdown syntax that comes back as a token, so nothing is imported.
 */
export function createTokenTransformer(tokens: TokenRegistry): TextMatchTransformer {
  return {
    dependencies: [TokenNode],
    export: (node, _exportChildren, exportFormat) => {
      const token = $isTokenNode(node) ? node.getToken() : null;
      if (token === null || !$isTokenNode(node)) return null;
      return exportFormat(node, tokens.markdown(token).text);
    },
    // Never matches: `(?!)` is a lookahead that always fails.
    importRegExp: /(?!)/,
    regExp: /(?!)/,
    type: "text-match",
  };
}

const childrenOf = (node: LexicalNode): LexicalNode[] =>
  $isElementNode(node) ? node.getChildren() : [];

/**
 * Export only: a table leaves as a GFM pipe table. Without this, upstream writes the text of every
 * cell run together on one line. Tables are named by type, not by class, because the table nodes
 * load on demand and this file is part of every editor; for the same reason there are no
 * `dependencies`, which upstream would insist were registered. Reading a Markdown table back in is
 * part of Markdown import, which is not built.
 *
 * A pipe table must start with a header row. A table whose first row is not one gets an empty
 * header, rather than having its first row of data promoted to headings.
 */
export const TABLE_EXPORT: ElementTransformer = {
  dependencies: [],
  export: (node, exportChildren) => {
    if (node.getType() !== "table") return null;
    const rows = childrenOf(node).map((row) =>
      childrenOf(row).map((cell) =>
        childrenOf(cell)
          .map((block) => ($isElementNode(block) ? exportChildren(block) : ""))
          // A cell is one line in a pipe table: its paragraphs and line breaks become `<br>`.
          .join("<br>")
          .replaceAll("\n", "<br>")
          .replaceAll("|", "\\|"),
      ),
    );
    const first = childrenOf(node)[0];
    const headed = first !== undefined && childrenOf(first).every(isHeaderRowCell);
    const width = Math.max(1, ...rows.map((row) => row.length));
    const line = (cells: readonly string[]): string =>
      `| ${Array.from({ length: width }, (_, index) => cells[index] ?? "").join(" | ")} |`;
    const head = headed ? rows[0]! : [];
    const body = headed ? rows.slice(1) : rows;
    return [line(head), line(Array.from({ length: width }, () => "---")), ...body.map(line)].join(
      "\n",
    );
  },
  // Never matches: `(?!)` is a lookahead that always fails.
  regExp: /(?!)/,
  replace: () => false,
  type: "element",
};

/**
 * Export only: an attachment leaves as a label. Named by type and read through JSON, because the
 * node loads on demand and this file is part of every editor.
 */
export const ATTACHMENT_EXPORT: ElementTransformer = {
  dependencies: [],
  export: (node) => {
    if (node.getType() !== ATTACHMENT_NODE_TYPE) return null;
    const attachment = readAttachment(node.exportJSON() as SerializedNodeLike);
    return attachment === undefined ? "" : attachmentMarkdown(attachment);
  },
  regExp: /(?!)/,
  replace: () => false,
  type: "element",
};

/** `headerState` bit 1: the cell is in a header row. Read through JSON, which every node has. */
function isHeaderRowCell(cell: LexicalNode): boolean {
  const state = (cell.exportJSON() as { headerState?: unknown }).headerState;
  return typeof state === "number" && (state & 1) !== 0;
}

// Longer delimiters first: `***` must be tried before `**` and `*`.
const INLINE: readonly Transformer[] = [
  INLINE_CODE,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  STRIKETHROUGH,
];

// CHECK_LIST before the list transformers so `- [ ] ` is not claimed as a bullet first.
const LISTS: readonly Transformer[] = [CHECK_LIST, UNORDERED_LIST, ORDERED_LIST];

const inCell = (parent: ElementNode, isImport: boolean): boolean =>
  !isImport && parent.getParent()?.getType() === "tablecell";

/**
 * A table cell holds paragraphs only, so the shortcuts that make another kind of block do nothing
 * there: `# ` stays `# `, as typed. Declining is upstream's own mechanism (a `replace` that
 * returns `false`), so nothing is converted and then converted back. Imports are left alone: they
 * never run inside a cell. Named by type because the table nodes load on demand.
 */
function outsideCells(transformer: Transformer): Transformer {
  if (transformer.type === "element") {
    const replace: ElementTransformer["replace"] = (parent, children, match, isImport) =>
      inCell(parent, isImport) ? false : transformer.replace(parent, children, match, isImport);
    return { ...transformer, replace };
  }
  if (transformer.type === "multiline-element") {
    const replace: MultilineElementTransformer["replace"] = (
      parent,
      children,
      start,
      end,
      lines,
      isImport,
    ) =>
      inCell(parent, isImport)
        ? false
        : transformer.replace(parent, children, start, end, lines, isImport);
    return { ...transformer, replace };
  }
  return transformer;
}

/**
 * `-[] `, `-[ ] `, and `-[x] `: the task shortcut typed without the space after the dash. It is not
 * Markdown (GFM wants `- [ ] `), so it is a typing shortcut only: never part of import, where a
 * line that starts `-[]` is a paragraph, and with nothing to export. The groups are the ones
 * CHECK_LIST's own `replace` reads: indentation, the box, and what is in it.
 */
const COMPACT_TASK: ElementTransformer = {
  dependencies: CHECK_LIST.dependencies,
  export: () => null,
  regExp: /^(\s*)[-*+](\[(\s|x)?\])\s/i,
  replace: CHECK_LIST.replace,
  type: "element",
};

/** What typing converts: everything Markdown does, and the shortcuts that are not Markdown. */
export function createTypingTransformers(
  preset: EditorPreset,
  validateUrl: (url: string) => boolean,
): Transformer[] {
  return [outsideCells(COMPACT_TASK), ...createTransformers(preset, validateUrl)];
}

export function createTransformers(
  preset: EditorPreset,
  validateUrl: (url: string) => boolean,
  tokens: TokenRegistry = CORE_TOKEN_REGISTRY,
): Transformer[] {
  const link = createLinkTransformer(validateUrl);
  const token = createTokenTransformer(tokens);
  const blocks: readonly Transformer[] =
    preset === "composer" ? [...LISTS, CODE] : [HEADING, QUOTE, ...LISTS, HORIZONTAL_RULE, CODE];
  return [TABLE_EXPORT, ATTACHMENT_EXPORT, ...blocks.map(outsideCells), ...INLINE, token, link];
}
